import React from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

import {Box} from './Surface';
import CustomLink from '../input/Link';
import Typography from '../stylized/Typography';

const DEFAULT_EMAIL_HEIGHT = 240;

const HtmlEmailViewer = (props) => {
  const {
    defaultExpanded = false,
    html = '',
    showLessLabel = 'Show less',
    showMoreLabel = 'Show more',
    text = '',
  } = props;

  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const [contentHeight, setContentHeight] = React.useState(DEFAULT_EMAIL_HEIGHT);

  const iframeRef = React.useRef(null);
  const resizeObserverRef = React.useRef(null);
  const animationFrameRef = React.useRef(null);

  const purifiedHtml = React.useMemo(() => {
    if (!html) {
      return '';
    }

    return DOMPurify.sanitize(html, {
      ADD_ATTR: ['target'],
      ADD_TAGS: ['style', 'head', 'html', 'body', 'meta', 'link'],
      USE_PROFILES: {html: true},
      WHOLE_DOCUMENT: true,
    });
  }, [html]);

  const hasBodyContent = Boolean(purifiedHtml || text);

  const cleanupResizeTracking = React.useCallback(() => {
    if (resizeObserverRef.current) {
      resizeObserverRef.current.disconnect();
      resizeObserverRef.current = null;
    }

    if (animationFrameRef.current) {
      window.cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  const resetViewer = React.useCallback(() => {
    cleanupResizeTracking();
    setContentHeight(DEFAULT_EMAIL_HEIGHT);
  }, [cleanupResizeTracking]);

  const syncIframeHeight = () => {
    const iframeDocument = iframeRef.current?.contentDocument;
    const iframeBody = iframeDocument?.body;
    const iframeHtml = iframeDocument?.documentElement;

    if (!iframeBody || !iframeHtml) {
      return;
    }

    const measuredHeight = Math.max(
        iframeBody.scrollHeight,
        iframeBody.offsetHeight,
        iframeHtml.scrollHeight,
        iframeHtml.offsetHeight,
    );

    if (measuredHeight > 0) {
      setContentHeight(measuredHeight);
    }
  };

  React.useEffect(() => {
    setIsExpanded(defaultExpanded);
    resetViewer();

    return () => {
      cleanupResizeTracking();
    };
  }, [cleanupResizeTracking, defaultExpanded, purifiedHtml, resetViewer]);

  React.useEffect(() => {
    if (!isExpanded) {
      resetViewer();
    }
  }, [isExpanded, resetViewer]);

  const onLoadIframe = () => {
    syncIframeHeight();
    cleanupResizeTracking();

    const iframeDocument = iframeRef.current?.contentDocument;
    const iframeBody = iframeDocument?.body;

    if (!iframeBody || typeof ResizeObserver === 'undefined') {
      return;
    }

    resizeObserverRef.current = new ResizeObserver(() => {
      if (animationFrameRef.current) {
        window.cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = window.requestAnimationFrame(() => {
        syncIframeHeight();
        animationFrameRef.current = null;
      });
    });

    resizeObserverRef.current.observe(iframeBody);
  };

  if (!hasBodyContent) {
    return null;
  }

  return (
    <React.Fragment>
      {
        isExpanded && (
          <>
            {
              purifiedHtml && (
                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                  }}
                >
                  <iframe
                    ref={iframeRef}
                    title='Email'
                    srcDoc={purifiedHtml}
                    onLoad={onLoadIframe}
                    style={{
                      border: 0,
                      display: 'block',
                      width: '100%',
                      height: `${Math.max(contentHeight, DEFAULT_EMAIL_HEIGHT)}px`,
                      backgroundColor: '#ffffff',
                    }}
                  />
                </Box>
              )
            }
            {
              !purifiedHtml && (
                <Typography
                  variant='body2'
                  sx={{
                    color: 'text.secondary',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  <Box component='span'>
                    {text}
                  </Box>
                </Typography>
              )
            }
          </>
        )
      }
      <Box>
        <CustomLink
          onClick={(event) => {
            event.preventDefault();
            setIsExpanded((previousValue) => !previousValue);
          }}
          underline='hover'
        >
          {isExpanded ? showLessLabel : showMoreLabel}
        </CustomLink>
      </Box>
    </React.Fragment>
  );
};

HtmlEmailViewer.propTypes = {
  defaultExpanded: PropTypes.bool,
  html: PropTypes.string,
  showLessLabel: PropTypes.node,
  showMoreLabel: PropTypes.node,
  text: PropTypes.string,
};

export default React.memo(HtmlEmailViewer);
